import jsQR from "jsqr";
import { useEffect, useRef, useState } from "react";

declare global {
  // BarcodeDetector (Chrome/Android); elders valt de scanner terug op jsQR.
  interface Window {
    BarcodeDetector?: new (o: { formats: string[] }) => {
      detect: (src: CanvasImageSource) => Promise<{ rawValue: string }[]>;
    };
  }
}

/**
 * QR-code scannen met de camera (achtercamera), met handmatige invoer als terugval
 * (geen camera, geen toestemming, of een code die niet scant).
 */
export function Scanner({ onResult, hint }: { onResult: (text: string) => void; hint?: string }) {
  const video = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState("");
  const done = useRef(false);
  const cb = useRef(onResult);
  cb.current = onResult;

  useEffect(() => {
    let stream: MediaStream | null = null;
    let raf = 0;
    let stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    const detector = window.BarcodeDetector ? new window.BarcodeDetector({ formats: ["qr_code"] }) : null;
    const hit = (text: string) => {
      if (done.current || !text) return;
      done.current = true;
      navigator.vibrate?.(60);
      cb.current(text);
    };
    async function tick() {
      const v = video.current;
      if (stopped || !v || !ctx) return;
      if (v.readyState >= 2 && v.videoWidth) {
        try {
          if (detector) {
            const r = await detector.detect(v);
            if (r[0]) return hit(r[0].rawValue);
          } else {
            const w = Math.min(640, v.videoWidth);
            const h = Math.round((v.videoHeight / v.videoWidth) * w);
            canvas.width = w;
            canvas.height = h;
            ctx.drawImage(v, 0, 0, w, h);
            const r = jsQR(ctx.getImageData(0, 0, w, h).data, w, h, { inversionAttempts: "dontInvert" });
            if (r?.data) return hit(r.data);
          }
        } catch {
          /* volgende frame */
        }
      }
      raf = requestAnimationFrame(() => void tick());
    }
    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Deze browser kan de camera niet gebruiken. Typ de code hieronder.");
        return;
      }
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" }, audio: false });
        if (stopped) return stream.getTracks().forEach((t) => t.stop());
        const v = video.current!;
        v.srcObject = stream;
        await v.play().catch(() => {});
        void tick();
      } catch {
        setError("Geen toegang tot de camera. Geef toestemming in de browser, of typ de code hieronder.");
      }
    })();
    return () => {
      stopped = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  return (
    <div className="scanner">
      <div className="scanview">
        <video ref={video} playsInline muted aria-label="Camerabeeld" />
        <span className="scanframe" aria-hidden="true" />
      </div>
      {hint && <p className="muted small">{hint}</p>}
      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <form
        className="pf-row"
        onSubmit={(e) => {
          e.preventDefault();
          if (manual.trim()) {
            done.current = true;
            onResult(manual.trim());
          }
        }}
      >
        <label className="field">
          <span className="label">Of typ de code / link</span>
          <input value={manual} onChange={(e) => setManual(e.target.value)} autoComplete="off" />
        </label>
        <button type="submit" className="btn">
          Zoeken
        </button>
      </form>
    </div>
  );
}
